import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { IPegaSecurityReportsState } from './pegaSecurityReports.model';
import {
  deletePegaSecurityReports,
  getPegaSecurityReportsById,
  savePegaSecurityReports,
  searchPegaSecurityReports,
} from './pegaSecurityReports.action';
import { IPegaSecurityReports } from '../../../services/pega/pegaSecurityReport/pegaSecurityReport.model';

export const initialState: IPegaSecurityReportsState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const pegaSecurityReportsSlice = createSlice({
  name: 'pegaSecurityReports',
  initialState,
  reducers: {
    clearPegaSecurityReports: () => {
      return initialState;
    },
    clearPegaSecurityReportsMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearPegaSecurityReportsGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchPegaSecurityReports.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchPegaSecurityReports.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchPegaSecurityReports.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getPegaSecurityReportsById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getPegaSecurityReportsById.fulfilled.type]: (
      state,
      action: PayloadAction<IPegaSecurityReports>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getPegaSecurityReportsById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [savePegaSecurityReports.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [savePegaSecurityReports.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [savePegaSecurityReports.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deletePegaSecurityReports.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deletePegaSecurityReports.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deletePegaSecurityReports.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const pegaSecurityReportsSelector = (state: RootState) => state.pegaSecurityReports;

// Actions
export const {
  clearPegaSecurityReports,
  clearPegaSecurityReportsMessages,
  clearPegaSecurityReportsGetById,
  setTableColumnSelection,
} = pegaSecurityReportsSlice.actions;

// The reducer
export default pegaSecurityReportsSlice.reducer;
